import { createFileRoute } from '@tanstack/react-router';
import Header from '@/components/Header/Header.tsx';
import {
  getGetIssuedTicketsWithTravellerByTransactionIDQueryKey,
  getGetTicketsForUserOrganizationQueryKey,
  getGetTransactionWithProductsQueryKey,
  useGetDepartmentByID,
  useGetIssuedTicketsWithTravellerByTransactionID,
  useGetOrganizationByID,
  useGetTransactionWithProducts,
  useRefundTransaction,
} from '@/api/trafikportalen';
import { FormattedMessage, useIntl } from 'react-intl';
import { useCallback, useState, useMemo } from 'react';
import { IconArrowRight, IconUndo } from '@/icons';
import Button from '@/components/core/Button.tsx';
import { ViewPermissions } from '@/components/Auth/ViewPermissions.tsx';
import { CenteredColumn } from '@/components/CenteredColumn.tsx';
import { WarningDialog } from '@/components/WarningDialog';
import { toast } from '@/hooks/useToast.tsx';
import { useQueryClient } from '@tanstack/react-query';
import { Loading } from '@/components/Loading';
import { DataNotFound } from '@/components/DataNotFound';
import { FormElement } from '@/components/FormElement';
import { formatDateTime } from '@/helpers/formatting';
import { useTransactionTranslations } from '@/hooks/useTransactionTranslations';
import { Section } from '@/components/Section';
import { checkUserPermissions } from '@/components/Auth/checkUserPermissions';
import { IssuedTicket } from '@/components/Reports/IssuedTicket';
import { DocumentHead } from '@/components/core/DocumentHead';

export const Route = createFileRoute(
  '/$tenantId/_layout/reports/transactions/$transactionId/',
)({
  component: TransactionDetails,
});

function TransactionDetails() {
  const params = Route.useParams();
  const { formatMessage, locale } = useIntl();
  const queryClient = useQueryClient();
  const { getStateTranslation, getSourceTranslation } =
    useTransactionTranslations();

  const { data: transaction, isLoading: transactionIsLoading } =
    useGetTransactionWithProducts(params.transactionId);

  const linkedTransactionId =
    transaction?.transaction.refund_transaction_id ??
    transaction?.transaction.refunded_transaction_id ??
    '';
  const { data: linkedTransaction, isLoading: linkedTransactionIsLoading } =
    useGetTransactionWithProducts(linkedTransactionId, {
      query: {
        enabled: !!linkedTransactionId,
      },
    });
  const { data: issuedTickets, isLoading } =
    useGetIssuedTicketsWithTravellerByTransactionID({
      transaction_id: params.transactionId,
    });
  const refundTransaction = useRefundTransaction();

  const { data: organization } = useGetOrganizationByID(
    transaction?.transaction.organization_id ?? '',
    {
      query: {
        enabled:
          checkUserPermissions({ permission: 'tenant:viewer' }) &&
          !!transaction?.transaction.organization_id,
      },
    },
  );

  const { data: department } = useGetDepartmentByID(
    transaction?.transaction.department_id ?? '',
    {
      query: {
        enabled:
          !checkUserPermissions({ permission: 'tenant:viewer' }) &&
          !!transaction?.transaction.department_id,
      },
    },
  );

  const [refundClicked, setRefundClicked] = useState<boolean>(false);

  const ticketBobcatOrderIds = useMemo(() => {
    const transactionOrderId = transaction?.transaction.bobcat_order_id;
    const orderIds = new Set<string>();

    for (const issuedTicket of issuedTickets ?? []) {
      const orderId = issuedTicket.ticket.bobcat_order_id;
      if (orderId && orderId !== transactionOrderId) {
        orderIds.add(orderId);
      }
    }

    return [...orderIds];
  }, [issuedTickets, transaction?.transaction.bobcat_order_id]);

  const metadataKeys = useMemo(() => {
    if (!transaction) {
      return {
        title: 'Transaktion',
        description: 'Transaktionsdetaljer',
      };
    }

    const productTitle = transaction.transaction_product.title;

    return {
      title: `Transaktion`,
      description: `Transaktionsdetaljer för ${productTitle} - ${transaction.transaction.amount_inc_vat} SEK`,
    };
  }, [transaction]);

  const renderFormContent = useCallback(() => {
    if (!transaction) {
      return null;
    }

    return formatMessage(
      {
        defaultMessage:
          'Är du säker på att du vill återbetala transaktionen på {amount} SEK inklusive moms? Observera att {ticket} inte automatiskt makuleras.',
      },
      {
        amount: transaction.transaction.amount_inc_vat.toLocaleString(locale),
        ticket:
          transaction.transaction_product.count > 1
            ? 'biljetterna'
            : 'biljetten',
      },
    );
  }, [formatMessage, transaction, locale]);

  const onSubmit = useCallback(() => {
    if (!transaction) {
      return;
    }

    refundTransaction.mutate(
      { transactionID: transaction.transaction.id },
      {
        onSuccess: async () => {
          toast.success(
            {
              title: formatMessage({
                defaultMessage: 'Transaktionen är återbetald.',
              }),
            },
            { autoClose: 3000 },
          );
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey: getGetTicketsForUserOrganizationQueryKey(),
            }),
            queryClient.invalidateQueries({
              queryKey: getGetTransactionWithProductsQueryKey(
                transaction.transaction.id,
              ),
            }),
            queryClient.invalidateQueries({
              queryKey: getGetIssuedTicketsWithTravellerByTransactionIDQueryKey(
                {
                  transaction_id: transaction.transaction.id,
                },
              ),
            }),
          ]);
          setRefundClicked(false);
        },
        onError: (error) => {
          console.error('Error: ', error);
          toast.error(
            {
              title: 'Ett fel inträffade',
              description:
                'Försök igen senare, eller vänligen kontakta support.',
            },
            { autoClose: 3000 },
          );
        },
      },
    );
  }, [transaction, refundTransaction, formatMessage, queryClient]);

  if (isLoading || transactionIsLoading) {
    return <Loading />;
  }

  if (!transaction) {
    return (
      <DataNotFound
        description={formatMessage({
          defaultMessage: 'Transaktionen hittades inte',
        })}
      />
    );
  }

  const {
    transaction: {
      state,
      amount_ex_vat: rawAmountExVat,
      amount_inc_vat: rawAmountIncVat,
      refund_transaction_id,
      id,
      created_at,
      source,
      bobcat_order_id,
      bobcat_transaction_id,
      reason,
    },
    transaction_product: { count, title, subtitle },
  } = transaction;

  const amount_ex_vat = state === 'refunded' ? -rawAmountExVat : rawAmountExVat;
  const amount_inc_vat =
    state === 'refunded' ? -rawAmountIncVat : rawAmountIncVat;

  return (
    <div className="overflow-y-auto">
      <DocumentHead
        title={metadataKeys.title}
        description={metadataKeys.description}
      />
      <Header
        title={formatMessage({ defaultMessage: 'Transaktionsdetaljer' })}
      />
      <CenteredColumn>
        {linkedTransactionIsLoading ? <Loading /> : null}
        {linkedTransaction && transaction.transaction.refund_transaction_id ? (
          <Section className="gap-2 border-yellow-500">
            <p className="text-lg font-bold text-text-900">
              <FormattedMessage defaultMessage="Återbetalning" />
            </p>
            <p className="text-text-700">
              <FormattedMessage defaultMessage="Denna transaktion har återbetalats" />
            </p>
            <p className="text-text-900">
              <FormattedMessage defaultMessage="Datum:" />{' '}
              {formatDateTime(linkedTransaction.transaction.created_at)}
            </p>
            <Button
              to="/$tenantId/reports/transactions/$transactionId"
              params={{
                transactionId: linkedTransaction.transaction.id,
                tenantId: params.tenantId,
              }}
              endIcon={<IconArrowRight aria-hidden="true" />}
            >
              <FormattedMessage defaultMessage="Visa återbetalning" />
            </Button>
          </Section>
        ) : null}

        <Section className="gap-2">
          <div className="mb-4 flex flex-col gap-x-2">
            <p className="text-sm text-text-700">
              <FormattedMessage defaultMessage="Internt ID:" />{' '}
              <span className="font-mono">{id}</span>
            </p>
            {bobcat_order_id ? (
              <p className="text-sm text-text-700">
                <FormattedMessage defaultMessage="Bobcat order-ID:" />{' '}
                <span className="font-mono">{bobcat_order_id}</span>
              </p>
            ) : null}
            {bobcat_transaction_id ? (
              <p className="text-sm text-text-700">
                <FormattedMessage defaultMessage="Bobcat transaktions-ID:" />{' '}
                <span className="font-mono">{bobcat_transaction_id}</span>
              </p>
            ) : null}
            {ticketBobcatOrderIds.map((orderId) => (
              <p key={orderId} className="text-sm text-text-700">
                <FormattedMessage defaultMessage="Biljettens Bobcat order-ID:" />{' '}
                <span className="break-all font-mono">{orderId}</span>
              </p>
            ))}
          </div>

          <FormElement
            label={formatMessage({ defaultMessage: 'Skapad:' })}
            input={<p>{formatDateTime(created_at)}</p>}
          />
          <FormElement
            label={formatMessage({ defaultMessage: 'Status:' })}
            input={
              <div>
                <p>{getStateTranslation({ state }) ?? '-'}</p>
                <p className="text-text-700">
                  {linkedTransaction &&
                  transaction.transaction.refunded_transaction_id ? (
                    <Button
                      to="/$tenantId/reports/transactions/$transactionId"
                      params={{
                        transactionId: linkedTransaction.transaction.id,
                        tenantId: params.tenantId,
                      }}
                      size="sm"
                      endIcon={<IconArrowRight aria-hidden="true" />}
                    >
                      <FormattedMessage defaultMessage="Ursprunglig transaktion" />
                    </Button>
                  ) : (
                    ''
                  )}
                </p>
              </div>
            }
          />
          <FormElement
            label={formatMessage({ defaultMessage: 'Källa:' })}
            input={<p>{getSourceTranslation({ source }) ?? '-'}</p>}
          />
          <FormElement
            label={formatMessage({ defaultMessage: 'Produkttitel:' })}
            input={<p>{count > 1 ? `${count}x ${title}` : title}</p>}
          />
          {reason && (
            <FormElement
              label={formatMessage({ defaultMessage: 'Syfte med resan:' })}
              input={<p>{reason}</p>}
            />
          )}
          {subtitle && (
            <FormElement
              label={formatMessage({ defaultMessage: 'Beskrivning:' })}
              input={<p>{subtitle}</p>}
            />
          )}

          <FormElement
            label={formatMessage({ defaultMessage: 'Inkl moms:' })}
            input={
              <p>
                {amount_inc_vat.toLocaleString(locale, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}{' '}
                <FormattedMessage defaultMessage="SEK" />
              </p>
            }
          />
          <FormElement
            label={formatMessage({ defaultMessage: 'Exkl moms:' })}
            input={
              <p>
                {amount_ex_vat.toLocaleString(locale, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}{' '}
                <FormattedMessage defaultMessage="SEK" />
              </p>
            }
          />
          {checkUserPermissions({ permission: 'tenant:editor' }) ? (
            <FormElement
              label={formatMessage({ defaultMessage: 'Organisation:' })}
              input={undefined}
              button={
                <Button
                  to="/$tenantId/organizations/organization/$organizationId"
                  params={{
                    organizationId: organization?.id ?? '',
                    tenantId: params.tenantId,
                  }}
                  size="sm"
                  className="w-full justify-between"
                  endIcon={<IconArrowRight aria-hidden="true" />}
                >
                  <span className="min-w-0 truncate">{organization?.name}</span>
                </Button>
              }
            />
          ) : (
            <FormElement
              label={formatMessage({ defaultMessage: 'Avdelning:' })}
              input={undefined}
              button={
                <Button
                  to="/$tenantId/departments/$departmentId"
                  params={{
                    departmentId: department?.id ?? '',
                    tenantId: params.tenantId,
                    view: 'information',
                  }}
                  size="sm"
                  className="w-full justify-between"
                  endIcon={<IconArrowRight aria-hidden="true" />}
                >
                  <span className="min-w-0 truncate">{department?.name}</span>
                </Button>
              }
            />
          )}

          {issuedTickets?.length ? (
            <FormElement
              label={formatMessage({ defaultMessage: 'Mottagare:' })}
              input={
                <div className="flex w-full flex-col gap-2">
                  {issuedTickets?.map((issuedTicket) => (
                    <IssuedTicket
                      key={issuedTicket.ticket.id}
                      issuedTicket={issuedTicket}
                      transaction={transaction}
                    />
                  ))}
                </div>
              }
            />
          ) : null}

          {state !== 'refunded' && !refund_transaction_id ? (
            <ViewPermissions permission="tenant:editor">
              <Button
                startIcon={<IconUndo />}
                variant="ghost"
                color="red"
                className="ml-auto mt-5"
                onClick={() => setRefundClicked(true)}
              >
                <FormattedMessage defaultMessage="Återbetala" />
              </Button>
            </ViewPermissions>
          ) : null}
        </Section>
        <WarningDialog
          title={formatMessage({
            defaultMessage: 'Återbetala transaktionen',
          })}
          content={renderFormContent()}
          onCancel={() => setRefundClicked(false)}
          onConfirm={onSubmit}
          buttonText={formatMessage({
            defaultMessage: 'Återbetala',
          })}
          buttonIcon={<IconUndo />}
          loading={refundTransaction.isPending}
          open={refundClicked}
        />
      </CenteredColumn>
    </div>
  );
}
